import Menu from '@/components/Menu';
import Popup from '@/components/Popup';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { font } from '@/theme/zeelool.theme';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Button, MenuProps, SxProps } from '@mui/material';
import { CheckOutlined, CollapseArrowDownOutlined, WapCollapseDownFilled } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import React, { useCallback, useContext, useEffect, useId, useMemo } from 'react';

export interface SortOption {
  label: string;
  value: string | number;
}

interface SortMenuProps {
  options: SortOption[];
  value?: string | number;
  onChange: (value: string | number) => void;
  showValue?: boolean;
  notShowValueContent?: React.ReactNode;
  buttonStyle?: React.CSSProperties;
  popupTitle?: string;
  popupHeight?: string | number;
  showColon?: boolean; // 是否显示冒号
  onSortBtnClick?: () => void;
  // 新增：位置配置
  anchorOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  transformOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  menuSlotProps?: MenuProps['slotProps'];
  menuSx?: SxProps;
}

export default function SortMenu({
  options,
  value,
  onChange,
  showValue = true,
  notShowValueContent,
  buttonStyle = {},
  popupTitle = '',
  popupHeight = 'auto',
  showColon = false,
  menuSlotProps = {},
  menuSx = {},
  onSortBtnClick,
  anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
  transformOrigin = { vertical: 'top', horizontal: 'left' },
}: SortMenuProps) {
  const t = useTranslations('sortMenu');
  const webMenuListId = useId();
  const wapSortListId = useId();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { deviceType } = useContext(ResponsiveContext);
  const [showPopup, setShowPopup] = React.useState(false);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (deviceType === EPlatform.WEB) {
        setAnchorEl(event.currentTarget);
      } else {
        setShowPopup(true);
      }
      onSortBtnClick?.();
    },
    [deviceType],
  );

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = useCallback(
    (_value: string | number) => {
      onChange(_value);
      handleClose();
      setShowPopup(false);
    },
    [onChange],
  );

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  // 当 popup 打开时，滚动到选中的选项
  useEffect(() => {
    if (showPopup && deviceType !== EPlatform.WEB && selectedOption) {
      // 使用 setTimeout 确保 DOM 已完全渲染
      setTimeout(() => {
        const selectedElement = document.getElementById(
          `${wapSortListId}-opt-${String(selectedOption.value)}`,
        );
        if (selectedElement) {
          selectedElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 100);
    }
  }, [showPopup, selectedOption, deviceType, wapSortListId]);

  return (
    <div>
      {showValue && (
        <>
          {deviceType === EPlatform.WEB && (
            <span className="text-[14px]">{`${t('sortBy')}${showColon ? ':' : ''}`}&nbsp;</span>
          )}
        </>
      )}
      <Button
        aria-expanded={deviceType === EPlatform.WEB ? open : showPopup}
        aria-haspopup={deviceType === EPlatform.WEB ? 'listbox' : 'dialog'}
        aria-controls={deviceType === EPlatform.WEB ? webMenuListId : wapSortListId}
        endIcon={
          deviceType === EPlatform.WEB ? (
            <CollapseArrowDownOutlined
              className={`!text-[16px] text-secondary-text transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
            />
          ) : (
            <WapCollapseDownFilled
              className={`!text-[16px] text-secondary-text transition-transform duration-300 ${showPopup ? 'rotate-180' : ''}`}
            />
          )
        }
        onClick={handleClick}
        sx={{
          color: 'text.primary',
          fontSize: '14px',
          '&:hover': {
            backgroundColor: 'transparent',
          },
          fontFamily: deviceType === EPlatform.WEB ? font.br : font.bb,
          '& .MuiButton-icon': {
            marginLeft: deviceType === EPlatform.WEB ? '8px' : '2px',
          },
          ...buttonStyle,
        }}
      >
        {showValue && <>{selectedOption?.label || ''}</>}
        {!showValue && notShowValueContent}
      </Button>
      <WebOnly>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          options={options}
          selectedValue={value}
          onSelect={handleSelect}
          // 不设置为true的话会有抖动
          disableScrollLock
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
          slotProps={{
            ...menuSlotProps,
            paper: {
              id: webMenuListId,
              ...(menuSlotProps?.paper && typeof menuSlotProps.paper === 'object'
                ? menuSlotProps.paper
                : {}),
            },
          }}
          sx={menuSx}
        />
      </WebOnly>
      <WapOnly>
        <Popup
          open={showPopup}
          onClose={() => setShowPopup(false)}
          position="bottom"
          title={popupTitle || t('title')}
          sx={{ '& .popup-content': { pt: 0, pb: 4 } }}
          height={popupHeight}
          contentContainerId={wapSortListId}
        >
          <div className="history-list" role="listbox" aria-label={popupTitle || t('title')}>
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={option.value === value}
                id={`${wapSortListId}-opt-${String(option.value)}`}
                className="flex w-full items-center justify-between h-[45px] border-b border-[#eeeeee] px-0 bg-transparent border-x-0 border-t-0 cursor-pointer text-left"
                onClick={() => handleSelect(option.value)}
              >
                <span
                  className={`text-[14px] leading-[45px] ${option.value === value && 'text-primary-purple font-bb'}`}
                >
                  {option.label}
                </span>
                {option.value === value && (
                  <CheckOutlined className="text-26 text-primary-purple" aria-hidden />
                )}
              </button>
            ))}
          </div>
        </Popup>
      </WapOnly>
    </div>
  );
}
